import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import type { BreadcrumbConfig } from "@/config/documentViewConfig";

type UseBreadcrumbParentCapacityOptions = {
  flatParents: BreadcrumbConfig[];
  headerRef: RefObject<HTMLElement | null>;
  listRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  remeasureKey: string;
};

/** Shrink when overflowing; never auto-increment (prevents 2↔3 oscillation at fit boundary). */
export const resolveNextMaxVisible = (
  prev: number,
  overflowing: boolean
): number => {
  if (overflowing && prev > 0) {
    return prev - 1;
  }
  return prev;
};

export const MIN_CURRENT_TITLE_WIDTH = 48;

export const isBreadcrumbRowOverCapacity = (
  list: HTMLElement,
  minCurrentTitleWidth = MIN_CURRENT_TITLE_WIDTH
): {
  overflowing: boolean;
  currentTooNarrow: boolean;
  currentTitleWidth: number;
  scrollWidth: number;
  clientWidth: number;
} => {
  const listOverflowing = list.scrollWidth > list.clientWidth + 1;
  const currentItem = list.querySelector<HTMLElement>(
    '[data-slot="breadcrumb-item"]:last-child'
  );
  const currentTitleWidth = currentItem?.clientWidth ?? 0;
  const currentTooNarrow = currentTitleWidth < minCurrentTitleWidth;

  return {
    overflowing: listOverflowing || currentTooNarrow,
    currentTooNarrow,
    currentTitleWidth,
    scrollWidth: list.scrollWidth,
    clientWidth: list.clientWidth,
  };
};

export const useBreadcrumbParentCapacity = ({
  flatParents,
  headerRef,
  listRef,
  enabled,
  remeasureKey,
}: UseBreadcrumbParentCapacityOptions): number => {
  const [maxVisible, setMaxVisible] = useState(flatParents.length);
  const prevHeaderWidthRef = useRef(0);

  const flatParentKey = flatParents
    .map((parent) => `${parent.url}:${parent.title}`)
    .join("|");

  useLayoutEffect(() => {
    setMaxVisible(flatParents.length);
    prevHeaderWidthRef.current = 0;
  }, [flatParents.length, flatParentKey]);

  useLayoutEffect(() => {
    if (!enabled || flatParents.length === 0) {
      return;
    }

    const measure = () => {
      const header = headerRef.current;
      const headerWidth = header?.clientWidth ?? 0;
      if (headerWidth > prevHeaderWidthRef.current + 8) {
        setMaxVisible(flatParents.length);
      }
      prevHeaderWidthRef.current = headerWidth;

      const container = listRef.current;
      const list =
        container?.querySelector<HTMLElement>('[data-slot="breadcrumb-list"]') ??
        container;
      if (!list) {
        return;
      }

      const { overflowing } = isBreadcrumbRowOverCapacity(list);

      setMaxVisible((prev) => resolveNextMaxVisible(prev, overflowing));
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    const header = headerRef.current;
    const list = listRef.current;

    if (header) {
      resizeObserver.observe(header);
    }
    if (list) {
      resizeObserver.observe(list);
    }

    return () => resizeObserver.disconnect();
  }, [enabled, flatParents.length, flatParentKey, remeasureKey, headerRef, listRef]);

  return maxVisible;
};
